import apiClient from './apiClient';

/** Keep canvas reads separate from thumbnail cache entries that lack CORS headers. */
export function corsReadUrl(src: string): string {
  try {
    const url = new URL(src);
    if (url.hostname !== 'storage.flobi.ai' || url.search) return src;
    url.searchParams.set('cors', '1');
    return url.toString();
  } catch {
    return src;
  }
}

/** Drive download routes need a Bearer token; a video/image element cannot attach one. */
export function needsMediaResolution(src: string, mediaAssetId?: string): boolean {
  try {
    const url = new URL(src, 'https://relative.invalid');
    return /\/(?:drive\/files|media\/assets)\/[^/]+\/(?:download|preview)\/?$/.test(url.pathname)
      || Boolean(mediaAssetId && url.searchParams.has('X-Amz-Signature'));
  } catch {
    return false;
  }
}

/**
 * Resolve only when bytes are needed, through our authenticated API client. Never send
 * a login token to a media URL or persist the temporary storage URL in the document.
 * Every new load (including reopening a file) asks for a fresh URL by stable media id.
 */
export async function readableMediaUrl(src: string, mediaAssetId?: string): Promise<string> {
  if (!needsMediaResolution(src, mediaAssetId)) return corsReadUrl(src);
  if (!mediaAssetId) throw new Error('This asset is missing its media reference. Add it again from My Stuff.');
  try {
    const response = await apiClient.post('/media/media.getSignedUrls', { mediaAssetIds: [mediaAssetId] });
    const envelope = response.data;
    if (envelope?.success === false) throw new Error('The media service could not prepare this asset. Try again.');
    const answer = envelope?.data ?? envelope;
    const url = answer?.urls?.[mediaAssetId];
    if (typeof url !== 'string' || !url) throw new Error('This asset is unavailable or you no longer have access to it.');
    const parsed = new URL(url);
    if (!['https:', 'http:'].includes(parsed.protocol) || needsMediaResolution(url)) {
      throw new Error('The media service did not return a playable file URL.');
    }
    return corsReadUrl(url);
  } catch (error) {
    const status = (error as { response?: { status?: number } }).response?.status;
    if (status === 401) throw new Error('Your sign-in expired. Sign in again to open this asset.');
    if (status === 403) throw new Error('You do not have access to this asset in the current workspace.');
    if (status === 404) throw new Error('This asset could not be found. It may have been removed.');
    if (status || (error as { isAxiosError?: boolean }).isAxiosError) throw new Error('The media service could not be reached. Try again.');
    throw error;
  }
}
